import type React from "react";
import type {
  DateValue,
  GroupProps,
  DateInputProps as RACDateInputProps,
  DatePickerProps as RACDatePickerProps,
} from "react-aria-components";

import { CalendarIcon } from "lucide-react";
import {
  composeRenderProps,
  Group,
  DateInput as RACDateInput,
  DatePicker as RACDatePicker,
  DateSegment as RACDateSegment,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Button } from "../button";
import { Calendar } from "../calendar/Calendar";
import { Description, FieldError, Label } from "../field";
import { Popover } from "../popover";
import {
  composeTailwindRenderProps,
  focusRingStyle,
  inputFieldStyle,
} from "../utils";

export interface DatePickerProps<T extends DateValue>
  extends RACDatePickerProps<T> {
  description?: string;
  errorMessage?: string;
  label?: string;
}

export function DatePicker<T extends DateValue>({
  description,
  errorMessage,
  label,
  ...props
}: DatePickerProps<T>) {
  return (
    <RACDatePicker
      {...props}
      className={composeTailwindRenderProps(props.className, [
        "w-full",
        ...inputFieldStyle,
      ])}
    >
      {label && <Label>{label}</Label>}
      <DatePickerGroup>
        <DateInput />
        <DatePickerButton />
      </DatePickerGroup>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <DatePickerPopover>
        <Calendar />
      </DatePickerPopover>
    </RACDatePicker>
  );
}

export function DatePickerGroup(props: GroupProps) {
  return (
    <Group
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        twMerge(
          "relative flex items-center w-full text-text-primary rounded-md border outline-hidden border-border-primary bg-bg-primary transition",
          "px-[14px] py-[10px]",
          "text-md",
          renderProps.isInvalid && "border-border-error",
          renderProps.isDisabled && "bg-bg-disabled-subtle",
          (renderProps.isFocusVisible || renderProps.isFocusWithin) &&
            focusRingStyle,
          className,
        ),
      )}
    />
  );
}

export function DateInput(props: Omit<RACDateInputProps, "children">) {
  return (
    <RACDateInput
      {...props}
      className={composeRenderProps(props.className, (className) =>
        twMerge("flex-1 flex items-center gap-0.5", className),
      )}
    >
      {(segment) => (
        <RACDateSegment
          className={composeRenderProps("", (className, renderProps) =>
            twMerge(
              "px-0.5 rounded-sm outline-hidden tabular-nums",
              renderProps.isFocused
                ? "bg-bg-brand-solid text-white"
                : "text-text-primary",
              renderProps.isPlaceholder ? "text-text-placeholder" : "",
              className,
            ),
          )}
          segment={segment}
        />
      )}
    </RACDateInput>
  );
}

export function DatePickerButton() {
  return (
    <Button
      className="ml-auto -mr-2 text-text-quaternary hover:text-text-primary"
      isIconOnly
      size="sm"
      variant="plain"
    >
      <CalendarIcon size={18} />
    </Button>
  );
}

export function DatePickerPopover({
  children,
  ...props
}: {
  children: React.ReactNode;
}) {
  return (
    <Popover {...props} className="p-4">
      {children}
    </Popover>
  );
}

export { Calendar as DatePickerCalendar } from "../calendar/Calendar";
